/**
 * OpenBoardFilterSheet — bottom sheet holding the board's three constraints: rank floor, entry
 * amount and challenge size.
 *
 * Selections apply immediately (the board behind updates as you tap), so the footer button is a
 * dismiss that reports the outcome — "Show 6 Challenges" — rather than a commit step. That keeps
 * the sheet honest: you can never apply a filter set you have not seen the result of.
 *
 * `focusSection` is the chip you arrived from: that section leads the sheet and its label is
 * highlighted, so a chip tap lands you somewhere specific instead of on a generic list.
 *
 * specs/open-challenges AC-17.
 */

import React, { useMemo } from 'react';
import {
  Modal, View, Text, Pressable, ScrollView, Platform,
} from 'react-native';
import { Image } from 'expo-image';
import { PlayerRankBadge } from '../../../src/economy/components/PlayerRankBadge/PlayerRankBadge';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets';
import {
  OPEN_BOARD_AMOUNT_OPTIONS,
  OPEN_BOARD_DEFAULT_BROWSE,
  OPEN_BOARD_RANK_OPTIONS,
  OPEN_BOARD_SIZE_OPTIONS,
  OPEN_BOARD_TEST_IDS,
} from '../../../src/challenges/constants/openBoard.constants';
import type {
  OpenBoardBrowseState,
  OpenBoardFilterSection,
} from '../../../src/challenges/types/openBoard.types';
import { getOpenBoardFilterSheetStyles } from './OpenBoardFilterSheet.styles';

interface OpenBoardFilterSheetProps {
  visible: boolean;
  browse: OpenBoardBrowseState;
  /** Cards matching the current selection — shown on the footer button. */
  resultCount: number;
  /** Section the chip that opened this asked for; it renders first. `null` = show default order. */
  focusSection?: OpenBoardFilterSection | null;
  onChange: (next: OpenBoardBrowseState) => void;
  onClose: () => void;
}

const SECTION_ORDER: readonly OpenBoardFilterSection[] = ['rank', 'amount', 'size'] as const;

export const OpenBoardFilterSheet: React.FC<OpenBoardFilterSheetProps> = ({
  visible,
  browse,
  resultCount,
  focusSection = null,
  onChange,
  onClose,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getOpenBoardFilterSheetStyles(theme), [theme]);

  const selectedAmount = OPEN_BOARD_AMOUNT_OPTIONS.find(
    (o) => o.minStake === browse.minStake && o.maxStake === browse.maxStake
  ) ?? OPEN_BOARD_AMOUNT_OPTIONS[0];

  // The asked-for section first; the rest keep their canonical order behind it.
  const sections = useMemo(
    () => (focusSection == null
      ? SECTION_ORDER
      : [focusSection, ...SECTION_ORDER.filter((s) => s !== focusSection)]),
    [focusSection]
  );

  const label = (section: OpenBoardFilterSection, text: string) => (
    <Text style={[styles.sectionLabel, focusSection === section && styles.sectionLabelFocused]}>
      {text}
    </Text>
  );

  const renderRank = () => (
    <View style={styles.section} key="rank">
      {label('rank', 'RANK')}
      <View style={styles.optionWrap}>
        {OPEN_BOARD_RANK_OPTIONS.map((opt) => {
          const selected = browse.rankFloor === opt.rankFloor;
          return (
            <Pressable
              key={opt.id}
              onPress={() => onChange({ ...browse, rankFloor: opt.rankFloor })}
              testID={`open-board-rank-${opt.id}`}
              accessibilityRole="button"
              aria-selected={selected}
              accessibilityState={{ selected }}
              style={({ pressed }) => [
                styles.option,
                selected && styles.optionSelected,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.optionPressed,
              ]}
            >
              {opt.rankFloor != null && (
                <PlayerRankBadge
                  rankIndex={opt.rankFloor}
                  size={16}
                  style={styles.optionBadge}
                />
              )}
              <Text style={[styles.optionLabel, selected && styles.optionLabelSelected]}>
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );

  const renderAmount = () => (
    <View style={styles.section} key="amount">
      <View style={styles.sectionHeaderRow}>
        {label('amount', 'ENTRY AMOUNT')}
        <Text style={styles.sectionValue}>{selectedAmount.label}</Text>
      </View>
      <View style={styles.optionWrap}>
        {OPEN_BOARD_AMOUNT_OPTIONS.map((opt) => {
          const selected = selectedAmount.id === opt.id;
          return (
            <Pressable
              key={opt.id}
              onPress={() => onChange({
                ...browse, minStake: opt.minStake, maxStake: opt.maxStake,
              })}
              testID={`open-board-amount-${opt.id}`}
              accessibilityRole="button"
              aria-selected={selected}
              accessibilityState={{ selected }}
              style={({ pressed }) => [
                styles.option,
                selected && styles.optionSelected,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.optionPressed,
              ]}
            >
              <Text style={[styles.optionLabel, selected && styles.optionLabelSelected]}>
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );

  const renderSize = () => (
    <View style={styles.section} key="size">
      {label('size', 'CHALLENGE SIZE')}
      <View style={styles.sizeRow}>
        {OPEN_BOARD_SIZE_OPTIONS.map((opt) => {
          const selected = browse.size === opt.id;
          return (
            <Pressable
              key={opt.id}
              onPress={() => onChange({ ...browse, size: opt.id })}
              testID={`open-board-size-${opt.id}`}
              accessibilityRole="button"
              aria-selected={selected}
              accessibilityState={{ selected }}
              style={({ pressed }) => [
                styles.sizeOption,
                selected && styles.optionSelected,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.optionPressed,
              ]}
            >
              <View style={styles.sizeLabelRow}>
                {opt.id === 'big' && (
                  <Image
                    source={{ uri: getCdnUrlExactKey(ASSET_PATHS.CROWN_ICON) }}
                    style={styles.sizeCrown}
                    contentFit="contain"
                    tintColor={selected ? theme.onPrimary : theme.accent}
                    alt={WEB_IMAGE_ALT_DECORATIVE}
                  />
                )}
                <Text style={[styles.optionLabel, selected && styles.optionLabelSelected]}>
                  {opt.label}
                </Text>
              </View>
              <Text style={[styles.sizeHint, selected && styles.sizeHintSelected]}>
                {opt.hint}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );

  const RENDERERS: Record<OpenBoardFilterSection, () => React.ReactNode> = {
    rank: renderRank,
    amount: renderAmount,
    size: renderSize,
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.scrim} onPress={onClose} accessibilityLabel="Close filters">
        {/* Stop taps inside the sheet from reaching the scrim behind it. */}
        <Pressable
          style={styles.sheet}
          onPress={(e) => e.stopPropagation()}
          testID={OPEN_BOARD_TEST_IDS.sheet}
        >
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.sheetContent}
          >
            <View style={styles.grabber} />

            <View style={styles.headerRow}>
              <Text style={styles.title}>Filters</Text>
              <Pressable
                onPress={() => onChange({ ...OPEN_BOARD_DEFAULT_BROWSE, sort: browse.sort })}
                testID={OPEN_BOARD_TEST_IDS.sheetReset}
                accessibilityRole="button"
                style={({ pressed }) => [pressed && styles.resetPressed]}
              >
                <Text style={styles.reset}>Reset</Text>
              </Pressable>
            </View>

            {sections.map((section) => RENDERERS[section]())}

            <Pressable
              onPress={onClose}
              testID={OPEN_BOARD_TEST_IDS.sheetApply}
              accessibilityRole="button"
              style={({ pressed }) => [styles.applyButton, pressed && styles.applyPressed]}
            >
              <Text style={styles.applyText}>
                Show {resultCount} {resultCount === 1 ? 'Challenge' : 'Challenges'}
              </Text>
            </Pressable>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};
